import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, isFunction } from 'lodash';
import styled from 'styled-components';
import {
  type EncodeStatusEnum,
  useCreateTranscriptionMutation,
} from '@frameio/graphql-client/generated';
import {
  Button2,
  Dialog,
  DialogActions,
  DialogFooter,
  theme,
  ToastAlert,
  useToast,
} from '@frameio/vapor';
import { createHttpClient } from '@frameio/web-uploader/src';
import { type LocaleKey, localeMap } from '../../components/LanguageMenu';
import {
  type CaptionEntries,
  type CaptionEntry,
  UploadCaptionsForm,
} from '../../components/UploadCaptionsForm/UploadCaptionsForm';
import { useActiveAssetId } from '../../contexts';
import { useMediaStateId } from '../../contexts/MediaViewerDataContext';
import { useCurrentPageType } from '../../routing';
import {
  getDerivedPlayerTypeAppDir,
  getDerivedSourceAppDir,
  useTrackUnsafe,
} from '../../segment/hooks';
import { useTranscriptionCacheOperations } from '../useTranscriptionCacheOperations';
import TEXT from './generated/TEXT/useManageUploadCaptionsDialog';

const WIDTH = 440;
const MOBILE_MAX_WIDTH = 377;
const MODAL_HEIGHT = 296;

TEXT.DEFINE({
  /** Add asset button label */
  actionLabel: 'Add to Asset',
  /** Cancel button label */
  cancel: 'Cancel',
  /** Warning shown when adding captions failed */
  failedToAddFiles: 'Failed to add Captions to Asset.',
  /** View button label */
  view: 'View',
});

type UploadCaptionsDialogProps = {
  assetId: string | null;
  isShowing: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
  setIsShowing: (assetId?: string | null, preserveErrors?: boolean) => void;
  captions: CaptionEntries;
  setCaptions: Dispatch<SetStateAction<CaptionEntries>>;
};
const mapFileToCaptionEntry = (file: File, i: number, isValid: boolean) => {
  return {
    id: i,
    locale: '',
    encodeStatus: 'UNKNOWN' as EncodeStatusEnum,
    file,
    isValid,
    hasOffset: false,
  };
};
export const useManageUploadCaptionsDialog = () => {
  const [isShowing, setIsShowing] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [captions, setCaptions] = useState<CaptionEntries>([]);
  const handleSetIsShowing = useCallback(
    (assetId?: string | null, preserveErrors?: boolean) => {
      setIsShowing(true);
      if (assetId) {
        setSelectedAssetId(assetId);
      }
      if (!preserveErrors) {
        setCaptions([]);
      }
    },
    []
  );
  const createUploadCaptionsDialog = useCallback(
    ({
      onClose,
      onSuccess,
    }: {
      onClose?: () => void;
      onSuccess?: (id: string) => void;
    } = {}) => (
      <UploadCaptionsDialog
        assetId={selectedAssetId}
        captions={captions}
        isShowing={isShowing}
        onClose={() => {
          onClose?.();
          setIsShowing(false);
        }}
        onSuccess={onSuccess}
        setCaptions={setCaptions}
        setIsShowing={handleSetIsShowing}
      />
    ),
    [captions, handleSetIsShowing, isShowing, selectedAssetId]
  );
  return {
    openUploadCaptionsDialog: handleSetIsShowing,
    createUploadCaptionsDialog,
    isShowing,
  };
};
export const UploadCaptionsDialog = ({
  assetId,
  isShowing,
  onClose,
  setIsShowing,
  captions,
  setCaptions,
  onSuccess,
}: UploadCaptionsDialogProps) => {
  const [getCaptionsUploadUrl] = useCreateTranscriptionMutation();
  const [uploadAttemptsCompleted, setUploadAttemptsCompleted] = useState(false);
  const { addToast } = useToast();
  const { handleWriteTranscriptionToCache } = useTranscriptionCacheOperations();
  const track = useTrackUnsafe();
  const mediaStateId = useMediaStateId();
  const pageType = useCurrentPageType();
  const derivedSource = getDerivedSourceAppDir(pageType);
  const activeAssetId = useActiveAssetId();
  const derivedPlayerType = activeAssetId
    ? getDerivedPlayerTypeAppDir(pageType, mediaStateId)
    : null;
  const canSubmit = useMemo(() => {
    const allFilesAreValid = (captions as CaptionEntries).every(
      (file: CaptionEntry) => {
        return !isEmpty(file?.locale) && file.isValid;
      }
    );
    return captions.length > 0 && allFilesAreValid;
  }, [captions]);
  const reOpenDialog = useCallback(
    (closeToast: () => void) => {
      closeToast();
      setIsShowing(assetId, true);
    },
    [assetId, setIsShowing]
  );
  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textFailedToAddFiles = TEXT.failedToAddFiles;
  const handleInvalidFiles = useCallback(
    (invalidFiles: File[], validFiles: File[]) => {
      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={textFailedToAddFiles}
            variant="error"
          />
        ),
        { duration: 3000 }
      );
      const filesWithValidity = [
        ...validFiles.map((file, i) => mapFileToCaptionEntry(file, i, true)),
        ...invalidFiles.map((file, i) => mapFileToCaptionEntry(file, i, false)),
      ];
      setCaptions((prevCaptions) => [...prevCaptions, ...filesWithValidity]);
    },
    [addToast, setCaptions, textFailedToAddFiles]
  );
  const updateCache = useCallback(
    (captionFile: CaptionEntry, status: EncodeStatusEnum, id: string) => {
      handleWriteTranscriptionToCache({
        asset: { id: assetId },
        id,
        encodeStatus: status,
        locale: captionFile?.locale,
        name: captionFile?.file?.name,
        displayName: localeMap?.[captionFile?.locale as LocaleKey]?.language,
        hasOffset: captionFile?.hasOffset,
        json: {
          streamUrl: null,
        },
        vtt: {
          downloadUrl: null,
        },
        srt: {
          downloadUrl: null,
        },
        text: {
          downloadUrl: null,
        },
        lastEditedAt: null,
        source: 'UPLOAD',
      });
    },
    [assetId, handleWriteTranscriptionToCache]
  );
  const setCaptionsWithErrors = useCallback(
    (index: number, id?: string | null | undefined) => {
      track('caption-upload-failed', {
        asset_id: assetId,
        locale: captions[index]?.locale,
        source: getDerivedSourceAppDir(pageType),
        player_type: derivedPlayerType,
        caption_file_type: captions[index]?.file?.type ? 'VTT' : 'SRT',
      });
      setCaptions((prevCaptions) => {
        const newCaptions = [...prevCaptions];
        newCaptions[index] = captions[index];
        newCaptions[index].isValid = false;
        return newCaptions.filter((caption) => !caption.isValid);
      });
      if (!id) return;
      updateCache(captions[index], 'FAILURE', id);
    },
    [
      assetId,
      captions,
      derivedPlayerType,
      pageType,
      setCaptions,
      track,
      updateCache,
    ]
  );
  const handleSubmit = useCallback(async () => {
    onClose();
    for await (const [index, captionFile] of captions.entries()) {
      if (!assetId) return;
      const captionFileType = captionFile.file.type || 'application/x-subrip';
      if (captionFile?.locale && captionFile.file) {
        await getCaptionsUploadUrl({
          variables: {
            assetId,
            locale: captionFile?.locale,
            mimeType: captionFileType,
            fileName: captionFile?.file.name,
            hasOffset: captionFile?.hasOffset,
          },
          onCompleted: (data) => {
            const signedUrl =
              data?.createTranscription?.transcription?.captionUploadUrl;
            if (!signedUrl) return;
            const id = data?.createTranscription?.transcription?.id;
            if (id) {
              updateCache(captionFile, captionFile.encodeStatus, id);
            }
            createHttpClient({
              onRetry: () => {
                // @todo (PLAY-5354): Handle retry logic if needed
              },
              headers: {
                'Content-Type': captionFileType,
              },
            })
              .put(signedUrl, captionFile.file)
              .then(() => {
                track('caption-upload-completed', {
                  assetId,
                  locale: captionFile?.locale,
                  source: derivedSource,
                  player_type: derivedPlayerType,
                  caption_upload_type:
                    captionFile?.file?.type === 'text/vtt' ? 'VTT' : 'SRT',
                });
                if (isFunction(onSuccess) && id) {
                  onSuccess(id);
                }
              })
              .catch(() => {
                setCaptionsWithErrors(index, id);
              });
          },
          onError: () => {
            setCaptionsWithErrors(index);
          },
        });
      }
    }
    setUploadAttemptsCompleted(true);
  }, [
    onClose,
    captions,
    assetId,
    getCaptionsUploadUrl,
    updateCache,
    onSuccess,
    track,
    derivedSource,
    derivedPlayerType,
    setCaptionsWithErrors,
  ]);
  useEffect(() => {
    if (
      !captions.every((caption) => (caption as CaptionEntry).isValid) &&
      uploadAttemptsCompleted
    ) {
      addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            adornmentAfter={
              <Button2
                onPress={() => reOpenDialog(closeToast)}
                variant="secondary"
              >
                {TEXT.view}
              </Button2>
            }
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={TEXT.failedToAddFiles}
            variant="error"
          />
        ),
        { duration: 3000 }
      );
      setUploadAttemptsCompleted(false);
    }
  }, [captions, reOpenDialog, uploadAttemptsCompleted, addToast]);
  const handleCancel = () => {
    onClose?.();
    setCaptions([]);
  };
  return (
    <StyledDialog isShowing={isShowing} onRequestClose={handleCancel}>
      <UploadCaptionsForm
        captions={captions}
        handleInvalidFiles={handleInvalidFiles}
        setCaptions={setCaptions}
      />
      <DialogFooter $showTopBorder={true}>
        <DialogActions>
          <Button2 onPress={handleCancel}>{TEXT.cancel}</Button2>
          <Button2
            data-testid="add-to-asset-button"
            isDisabled={!canSubmit}
            onPress={handleSubmit}
            variant={!canSubmit ? 'secondary' : 'primary'}
          >
            {TEXT.actionLabel}
          </Button2>
        </DialogActions>
      </DialogFooter>
    </StyledDialog>
  );
};
const StyledDialog = styled(Dialog)`
  max-width: ${MOBILE_MAX_WIDTH}px;
  min-height: ${MODAL_HEIGHT}px;

  @media (min-width: ${theme.breakpoints.tablet}) {
    max-width: ${WIDTH}px;
  }
`;
